import {
  Button,
  HStack,
  Icon,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Text,
  useBoolean,
} from '@elkaso-app/web-design';
import { usePageParams } from 'hooks/use-page-params';
import { useEffect, useMemo } from 'react';
import { FiCheck, FiSliders } from 'react-icons/fi';

type SortKey = 'entityName' | 'brandName' | 'tradeLicenseName' | 'dedName' | 'createdAt';
type SortOrder = 'asc' | 'desc';

type SortOption = {
  key: SortKey;
  order: SortOrder;
  label: string;
};

const options: SortOption[] = [
  { key: 'entityName', order: 'asc', label: 'Entity Name A → Z' },
  { key: 'entityName', order: 'desc', label: 'Entity Name Z → A' },
  { key: 'brandName', order: 'asc', label: 'Brand Name A → Z' },
  { key: 'brandName', order: 'desc', label: 'Brand Name Z → A' },
  { key: 'tradeLicenseName', order: 'asc', label: 'Trade License A → Z' },
  { key: 'tradeLicenseName', order: 'desc', label: 'Trade License Z → A' },
  { key: 'dedName', order: 'asc', label: 'DED Name A → Z' },
  { key: 'dedName', order: 'desc', label: 'DED Name Z → A' },
  { key: 'createdAt', order: 'asc', label: 'Created At ↑ (oldest first)' },
  { key: 'createdAt', order: 'desc', label: 'Created At ↓ (newest first)' },
];

export const SortMenuButton = () => {
  const [isActive, setIsActive] = useBoolean(false);
  const { getPageParams, setPageParams } = usePageParams();
  const params = getPageParams();

  useEffect(() => {
    if (params.sort) setIsActive.on();
    else setIsActive.off();
  }, [params.sort, setIsActive]);

  const currentLabel = useMemo(() => {
    const current = (params.sort as string) || '';
    if (!current) return '';
    const [key, order] = current.split('.') as [SortKey, SortOrder];
    const match = options.find((o) => o.key === key && o.order === order);
    return match?.label || '';
  }, [params.sort]);

  const applySort = (key: SortKey, order: SortOrder) => {
    const newParams: any = { ...params };
    newParams.sort = `${key}.${order}`;
    setPageParams(newParams);
  };

  const clearSort = () => {
    const newParams: any = { ...params };
    newParams.sort = '';
    setPageParams(newParams);
  };

  return (
    <Menu isLazy>
      <MenuButton
        as={Button}
        size='sm'
        variant={isActive ? 'solid' : 'outline'}
        colorScheme={isActive ? 'red' : 'gray'}
        leftIcon={<Icon as={FiSliders} />}>
        <HStack spacing='2' align='center'>
          <Text>Sort</Text>
          {currentLabel && (
            <Text fontSize='sm' color='gray.100' _dark={{ color: 'gray.300' }} noOfLines={1}>
              {currentLabel}
            </Text>
          )}
        </HStack>
      </MenuButton>
      <MenuList minW='260px'>
        {options.map((opt) => {
          const isCurrent = currentLabel === opt.label;
          return (
            <MenuItem key={`${opt.key}.${opt.order}`} onClick={() => applySort(opt.key, opt.order)}>
              {isCurrent && <Icon as={FiCheck} mr={2} />} {opt.label}
            </MenuItem>
          );
        })}
        <MenuDivider />
        <MenuItem onClick={clearSort}>Clear sorting</MenuItem>
      </MenuList>
    </Menu>
  );
};
